import { useState, useEffect, useRef } from 'react';
import { Download, Copy, Plus, Check, ThumbsUp, FileText, AlertCircle, Send } from 'lucide-react';

// Ctrl-number shortcuts (must match the legend in DictationPanel):
//   Ctrl+2 = Send to Karisma, Ctrl+3 = New report.
const COPY_KARISMA_KEY = '2';
const NEW_REPORT_KEY = '3';
import { useReportStore } from '../../stores/reportStore';
import { exportApi, templateApi, editTrackingApi } from '../../services/api';
import {
  htmlToPlainText,
  buildClipboardHtml,
  copyPlainText,
  copyRichHtml,
} from '../../lib/reportClipboard';
import FormatSelector from './FormatSelector';

export default function ExportPanel() {
  const {
    editedReport,
    generatedReport,
    transcription,
    resetSession,
    patientIdentifier,
    studyDate,
    modality,
    bodyPart,
    examCategory,
    templateId,
    templateName,
    selectedModel,
    decisionId,
  } = useReportStore();
  const [exportFormat, setExportFormat] = useState<'rtf' | 'hl7' | 'text'>('rtf');
  const [copied, setCopied] = useState<'idle' | 'ok' | 'failed'>('idle');
  const [copiedRich, setCopiedRich] = useState<'idle' | 'ok' | 'failed'>('idle');
  const [isExporting, setIsExporting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  // Send to Karisma. PROVEN 2026-06-23 against real Karisma (TMS WPTools):
  //  - HTML clipboard CRASHES Karisma's importer ("peek 0 characters" in
  //    TAdvHTMLParser.ConsumeHeading) — so we must NEVER put HTML on the
  //    clipboard for Karisma;
  //  - plain text pastes but loses bold;
  //  - true RTF pastes PERFECTLY (bold headings + spacing).
  // A browser cannot write an RTF clipboard flavor — only the native Karisma
  // Bridge helper can. So: write Karisma-safe PLAIN text as the floor, then
  // ask the bridge to upgrade the clipboard to RTF (and optionally auto-paste).
  // If the bridge isn't installed, the plain text is the graceful fallback.
  const [karismaState, setKarismaState] = useState<'idle' | 'busy' | 'pasted' | 'copied' | 'failed'>('idle');
  const [karismaError, setKarismaError] = useState('');
  const bridgeRequestId = useRef(0);

  const handleSendToKarisma = async () => {
    if (!editedReport || karismaState === 'busy') return;
    setKarismaState('busy');
    setKarismaError('');

    let plainCopied = false;
    try {
      await copyPlainText(htmlToPlainText(editedReport));
      plainCopied = true;
    } catch {
      /* secure-context/permission issue — the bridge may still succeed */
    }

    try {
      const resp = await exportApi.clipboard(editedReport, decisionId || undefined);
      const requestId = ++bridgeRequestId.current;
      const result = await new Promise<any>((resolve, reject) => {
        const timer = setTimeout(() => {
          window.removeEventListener('message', onResult);
          reject(new Error('Bridge did not respond — install the helper, then restart the browser.'));
        }, 8000);
        const onResult = (event: MessageEvent) => {
          if (event.source !== window || !event.data) return;
          if (event.data.type === 'AXIOM_KB_RESULT' && event.data.requestId === requestId) {
            clearTimeout(timer);
            window.removeEventListener('message', onResult);
            resolve(event.data);
          }
        };
        window.addEventListener('message', onResult);
        window.postMessage({
          type: 'AXIOM_KB_SEND',
          requestId,
          rtf: resp.rtf || '',                  // true RTF — bold + spacing in Karisma
          text: htmlToPlainText(editedReport),  // plain-text flavor
        }, '*');
      });

      if (result.ok && result.pasted) {
        setKarismaState('pasted');              // bridge auto-pasted true RTF
      } else if (result.ok) {
        setKarismaState('copied');              // true RTF on clipboard — paste manually
      } else if (plainCopied) {
        setKarismaState('copied');              // bridge not reachable; plain text is on the clipboard
      } else {
        setKarismaState('failed');
        setKarismaError(result.error || 'Unknown bridge error');
      }
    } catch (err) {
      if (plainCopied) {
        setKarismaState('copied');
      } else {
        setKarismaState('failed');
        setKarismaError(err instanceof Error ? err.message : 'Send failed');
      }
    }
    setTimeout(() => { setKarismaState('idle'); setKarismaError(''); }, 5000);
    logCopySignals();
  };

  /** Fire-and-forget logging that must NEVER delay or break a copy:
   *  the server-side copy signal + the edit pair for learning. */
  const logCopySignals = () => {
    exportApi.clipboard(editedReport, decisionId || undefined).catch(() => {});
    if (generatedReport) {
      editTrackingApi.record({
        generated_report: generatedReport,
        final_report: editedReport,
        transcription: transcription || undefined,
        decision_id: decisionId || undefined,
        modality: modality || undefined,
        body_part: bodyPart || undefined,
        exam_category: examCategory || undefined,
        template_id: templateId || undefined,
        template_name: templateName || undefined,
        model_used: selectedModel || undefined,
        trigger: 'clipboard',
      }).catch(() => {});
    }
  };

  /**
   * Main copy — for Karisma (Kestral RIS). PLAIN TEXT ONLY, CRLF line
   * endings, blank line between paragraphs, tight after headings.
   * Karisma's paste parser errors on rich/constructed HTML flavors, so we
   * deliberately put nothing but text on the clipboard.
   * The clipboard write happens synchronously inside the click gesture —
   * no awaited fetches first (transient user activation expires otherwise,
   * which is why this button used to fail intermittently and silently).
   */
  const handleCopyToClipboard = async () => {
    if (!editedReport) return;
    try {
      const plainText = htmlToPlainText(editedReport); // CRLF by default
      await copyPlainText(plainText);
      setCopied('ok');
      setTimeout(() => setCopied('idle'), 2000);
      logCopySignals();
    } catch (error) {
      console.error('Clipboard copy failed:', error);
      setCopied('failed');
      setTimeout(() => setCopied('idle'), 3000);
    }
  };

  /** Secondary copy — rich HTML for Word (double line spacing inline). */
  const handleCopyForWord = async () => {
    if (!editedReport) return;
    try {
      const plainText = htmlToPlainText(editedReport);
      const richHtml = buildClipboardHtml(editedReport);
      await copyRichHtml(richHtml, plainText);
      setCopiedRich('ok');
      setTimeout(() => setCopiedRich('idle'), 2000);
      logCopySignals();
    } catch (error) {
      console.error('Rich clipboard copy failed:', error);
      setCopiedRich('failed');
      setTimeout(() => setCopiedRich('idle'), 3000);
    }
  };

  const handleAcceptTemplate = async () => {
    if (!decisionId) return;

    try {
      await templateApi.recordSignal(decisionId, 'accept_template');
      setAccepted(true);
      setTimeout(() => setAccepted(false), 3000);

      // Fire-and-forget: track edit for AI personalization
      if (generatedReport) {
        editTrackingApi.record({
          generated_report: generatedReport,
          final_report: editedReport || generatedReport,
          transcription: transcription || undefined,
          decision_id: decisionId || undefined,
          modality: modality || undefined,
          body_part: bodyPart || undefined,
          exam_category: examCategory || undefined,
          template_id: templateId || undefined,
          template_name: templateName || undefined,
          model_used: selectedModel || undefined,
          trigger: 'accept_template',
        }).catch(() => {});
      }
    } catch (error) {
      console.error('Failed to record template acceptance:', error);
    }
  };

  const handleExport = async () => {
    if (!editedReport) return;

    setIsExporting(true);
    try {
      const metadata = {
        patient_name: patientIdentifier,
        patient_id: patientIdentifier,
        study_date: studyDate,
        modality,
      };

      let blob: Blob;
      let filename: string;

      switch (exportFormat) {
        case 'rtf':
          blob = await exportApi.rtf(editedReport, metadata);
          filename = `report_${Date.now()}.rtf`;
          break;
        case 'hl7':
          blob = await exportApi.hl7(editedReport, metadata);
          filename = `report_${Date.now()}.hl7`;
          break;
        case 'text':
        default:
          blob = await exportApi.text(editedReport, metadata);
          filename = `report_${Date.now()}.txt`;
          break;
      }

      // Download the file
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // Fire-and-forget: track edit for AI personalization
      if (generatedReport) {
        editTrackingApi.record({
          generated_report: generatedReport,
          final_report: editedReport,
          transcription: transcription || undefined,
          decision_id: decisionId || undefined,
          modality: modality || undefined,
          body_part: bodyPart || undefined,
          exam_category: examCategory || undefined,
          template_id: templateId || undefined,
          template_name: templateName || undefined,
          model_used: selectedModel || undefined,
          trigger: 'download',
        }).catch(() => {});
      }
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleNewReport = () => {
    if (editedReport && !confirm('Start a new report? Current changes will be lost.')) {
      return;
    }
    resetSession();
  };

  // Ctrl-number shortcuts: Ctrl+2 = Send to Karisma, Ctrl+3 = New report.
  // Ctrl combos fire even while editing the report.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!e.ctrlKey || e.altKey || e.metaKey || e.shiftKey) return;
      if (e.key === COPY_KARISMA_KEY) {
        e.preventDefault();
        if (editedReport) handleSendToKarisma();
      } else if (e.key === NEW_REPORT_KEY) {
        // Reset directly — the hotkey is a deliberate action, and a native
        // confirm() can be suppressed by Chrome ("prevent additional dialogs"),
        // which would silently abort. The New Report *button* still confirms.
        e.preventDefault();
        resetSession();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedReport]);

  return (
    <div className="flex flex-wrap items-center justify-end gap-2 min-w-0">
        <span className="text-sm text-gray-500 dark:text-gray-400">Export:</span>

        <FormatSelector value={exportFormat} onChange={setExportFormat} />

        <button
          onClick={handleExport}
          disabled={!editedReport || isExporting}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Download size={16} />
          {isExporting ? 'Exporting...' : 'Download'}
        </button>

        {/* Send to Karisma — delivers TRUE RTF (bold headings + spacing) via
            the native Karisma Bridge helper, the only way to put RTF on the
            clipboard (browsers can't, and Karisma's HTML importer crashes).
            Falls back to Karisma-safe plain text when the bridge isn't
            installed. */}
        <button
          onClick={handleSendToKarisma}
          disabled={!editedReport || karismaState === 'busy'}
          title={karismaError || 'Send the report to Karisma as true RTF (bold + spacing) via the Axiom Karisma Bridge'}
          className={`flex items-center gap-2 px-4 py-2 disabled:opacity-50 text-sm font-medium rounded-lg transition-colors ${
            karismaState === 'failed'
              ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
              : karismaState === 'pasted' || karismaState === 'copied'
              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
              : 'bg-teal-600 hover:bg-teal-700 text-white'
          }`}
        >
          {karismaState === 'busy' ? (
            <>Sending…</>
          ) : karismaState === 'pasted' ? (
            <>
              <Check size={16} />
              Pasted into Karisma
            </>
          ) : karismaState === 'copied' ? (
            <>
              <Check size={16} />
              Copied — Ctrl+V in Karisma
            </>
          ) : karismaState === 'failed' ? (
            <>
              <AlertCircle size={16} />
              Send failed
            </>
          ) : (
            <>
              <Send size={16} />
              Send to Karisma
            </>
          )}
        </button>

        {/* Plain-text copy (CRLF, Karisma-safe spacing). Deliberately NOT
            named "Karisma": the only Karisma-branded button is the teal
            bridge one, so there is exactly one obvious Karisma path when the
            bridge is installed — and no Karisma mention at all without it.
            (Browsers cannot write an RTF clipboard flavor, and Karisma's
            paste parser errors on constructed HTML — so this stays plain
            text only. True RTF: bridge button or Download → RTF.) */}
        <button
          onClick={handleCopyToClipboard}
          disabled={!editedReport}
          title="Plain text, CRLF, blank line between paragraphs — safe to paste anywhere"
          className={`flex items-center gap-2 px-4 py-2 disabled:opacity-50 text-sm font-medium rounded-lg transition-colors ${
            copied === 'failed'
              ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
              : 'bg-gray-100 dark:bg-surface-800 hover:bg-gray-200 dark:hover:bg-surface-850 text-gray-700 dark:text-gray-200'
          }`}
        >
          {copied === 'ok' ? (
            <>
              <Check size={16} className="text-green-600" />
              Copied!
            </>
          ) : copied === 'failed' ? (
            <>
              <AlertCircle size={16} />
              Copy failed
            </>
          ) : (
            <>
              <Copy size={16} />
              Copy to Clipboard
            </>
          )}
        </button>

        <button
          onClick={handleCopyForWord}
          disabled={!editedReport}
          title="Rich formatting with double line spacing — for pasting into Word"
          className={`flex items-center gap-2 px-3 py-2 disabled:opacity-50 text-sm font-medium rounded-lg transition-colors ${
            copiedRich === 'failed'
              ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
              : 'bg-gray-100 dark:bg-surface-800 hover:bg-gray-200 dark:hover:bg-surface-850 text-gray-700 dark:text-gray-200'
          }`}
        >
          {copiedRich === 'ok' ? (
            <>
              <Check size={16} className="text-green-600" />
              Copied!
            </>
          ) : copiedRich === 'failed' ? (
            <>
              <AlertCircle size={16} />
              Failed
            </>
          ) : (
            <>
              <FileText size={16} />
              Copy for Word
            </>
          )}
        </button>

        {/* Accept Template button - only show when there's a decision */}
        {decisionId && templateName && (
          <button
            onClick={handleAcceptTemplate}
            disabled={accepted}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {accepted ? (
              <>
                <Check size={16} />
                Accepted
              </>
            ) : (
              <>
                <ThumbsUp size={16} />
                Accept Template
              </>
            )}
          </button>
        )}

      {/* New Report Button */}
      <button
        onClick={handleNewReport}
        className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors"
      >
        <Plus size={16} />
        New Report
      </button>
    </div>
  );
}
